import { useSnackbar } from 'notistack';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import { Link, useParams } from 'react-router-dom';

import configUrls from '../../config/configUrls';
import { useEffect, useState } from 'react';
import { Processo } from '../../types/processo/Processo';
import { AxiosError } from 'axios';
import axiosInstance from '../../app/axiosInstance';
import { ErrorMessage } from '../../types/ErrorMessage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DOMPurify from 'dompurify';
import { format } from 'date-fns';

export const ProcessoList = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const [processos, setProcessos] = useState<Processo[]>([]);

  const [isGerados, setIsGerados] = useState(true);
  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsGerados(e.target.checked);
  };

  const fetchDadosRecebidos = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/processo/recebidos?search=`);
      setProcessos(response.data);
    } catch (error: unknown) {
      setProcessos([]);
      const axiosError = error as AxiosError;
      console.log(error);
      enqueueSnackbar('Erro ao efetuar a consulta. Tente novamente mais tarde', { variant: 'error' });

      let erro: ErrorMessage | undefined;
      if (axiosError.response) {
        erro = axiosError.response.data as ErrorMessage;
      }
      if (erro && erro.message) {
        enqueueSnackbar(erro.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Mensagem de erro não disponível', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDadosGerados = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/processo/gerados?search=`);
      setProcessos(response.data);
    } catch (error: unknown) {
      setProcessos([]);
      const axiosError = error as AxiosError;
      enqueueSnackbar('Erro ao efetuar a consulta. Tente novamente mais tarde', { variant: 'error' });

      let erro: ErrorMessage | undefined;
      if (axiosError.response) {
        erro = axiosError.response.data as ErrorMessage;
      }
      if (erro && erro.message) {
        enqueueSnackbar(erro.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Mensagem de erro não disponível', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isGerados) {
      fetchDadosGerados();
    } else {
      fetchDadosRecebidos();
    }
  }, [isGerados]);

  if (loading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  const header = (
    <>
      <div className="flex flex-wrap justify-content-end gap-2">
        <span>Processo(s) encontrado(s): {processos.length}</span>
      </div>
    </>
  );

  const renderAction = (rowData: any) => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <Tooltip title="Detalhes">
          <Link to={`${configUrls.GRUPO_ACOMPANHAMENTO_EDIT_LK}${rowData.id}`}>
            <IconButton color="secondary" aria-label="detais">
              <InfoOutlinedIcon />
            </IconButton>
          </Link>
        </Tooltip>
      </Box>
    );
  };

  const renderData = (rowData: any) => {
    const proc = rowData as Processo;
    const dataCriacao = proc.dataHoraCriacao ? format(proc.dataHoraCriacao, 'dd/MM/yyyy HH:mm:ss') : 'Data não disponível';

    return (
      <Box>
        <Link to={`${configUrls.PROCESSO_FORM_VIEW_LK}${rowData.id}`}>
          <Box>
            <Typography color="secondary" variant="body2" gutterBottom>
              {dataCriacao}
            </Typography>
          </Box>
        </Link>
      </Box>
    );
  };

  const renderToolTip = (rowData: any) => {
    const proc = rowData as Processo;
    const tipo = proc.tipoProcesso?.nome ? 'Tipo: ' + proc.tipoProcesso.nome : null;
    const desc = proc.especificacao ? 'Desc: ' + proc.especificacao : null;
    const obs = proc.observacao ? 'Obs: ' + proc.observacao : null;
    const usuario = proc.usuario?.nome ? 'Usuario: ' + proc.usuario?.nome : null;

    const content = (
      <Box
        sx={{
          padding: '5px',
          backgroundColor: '#ffff',
          color: '#000',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%', // Preenche toda a largura do Box pai
          height: '100%', // Preenche toda a altura do Box pai
          overflow: 'auto',
          border: '1px solid #000',
          borderRadius: '8px'
        }}
      >
        {tipo && (
          <Box sx={{ backgroundColor: '#00008B', color: '#fff' }} id="tipo">
            {tipo}
          </Box>
        )}
        {desc && <Box id="desc">{desc}</Box>}
        {obs && <Box id="obs">{obs}</Box>}
        {usuario && <Box id="Usuário:">{usuario}</Box>}
      </Box>
    );

    return (
      <Box>
        <Tooltip arrow title={content}>
          <Link to={`${configUrls.PROCESSO_FORM_VIEW_LK}${rowData.id}`}>
            <Box>
              <Typography color="secondary" variant="body2" gutterBottom>
                {rowData.numeroProcesso}
              </Typography>
            </Box>
          </Link>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <Paper sx={{ width: '100%', height: '100vh', padding: '12px', display: 'flex', flexDirection: 'column' }}>
        <Grid container padding={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Box display="flex">
                  <Typography color="secondary" variant="h6" gutterBottom>
                    Lista de Processos
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="flex-end">
                  <Button variant="contained" color="secondary" component={Link} to={configUrls.PROCESSO_CREATE_RT}>
                    Novo Processo
                  </Button>
                </Box>
              </Box>
            </FormControl>
          </Grid>

          <Grid item xs={12} mt={3}>
            <Box display="flex">
              <FormGroup>
                <FormControlLabel
                  control={<Switch name="ativo" color="secondary" checked={isGerados} onChange={handleToggle} inputProps={{ 'aria-label': 'controlled' }} />}
                  label={isGerados ? ' Processos Gerados' : 'Processo Recebidos'}
                />
              </FormGroup>
            </Box>
          </Grid>
        </Grid>

        <DataTable value={processos} paginator rows={25} showGridlines={true} size="small" columnResizeMode="expand" resizableColumns dataKey="id" header={header} className="custom-datatable">
          <Column field="data" header="Data" body={renderData} sortable style={{ width: '30%' }}></Column>
          <Column field="id" header="Processo" body={renderToolTip} sortable style={{ width: '30%' }}></Column>

          <Column field="descricao" header="Responsável" sortable style={{ width: '30%' }}></Column>
          <Column field="detalhes" header="Detalhes" body={renderAction} style={{ width: 'auto' }}></Column>
        </DataTable>
      </Paper>
    </>
  );
};
